/**
 * BulkAuditAssignModal.jsx
 *
 * ─ Single API call on modal open (locations + existing config fetched together)
 * ─ KPI counts are hyperlinks → opens KpiDrawer (right-side panel)
 * ─ Drawer shows: question title + who is assigned at which level & location
 * ─ Groups KPIs by "next available level" (computed client-side after open)
 * ─ Same auditor cannot appear at two levels for the same KPI + location
 * ─ Sequential levels only (L1 before L2, L2 before L3)
 * ─ Existing assignment rows in ExistingSummary also open KpiDrawer
 */

import { useState, useEffect, useRef, useMemo } from "react";
import { apiCall } from "../../../../../_services/apiCall";
import config from "../../../../../config/config.json";
import { useCurrentUser } from "../../../../../hooks/useCurrentUser";

const MAX_LEVELS = 3;
const ROLE_IDS   = { INTERNAL: "4", EXTERNAL: "5" };

/* ════════════════════════════════════════════════
   DESIGN TOKENS
════════════════════════════════════════════════ */
const T = {
  blue:   "#2563eb", blueL: "#eff6ff",   blueB:   "#bfdbfe",
  purple: "#7c3aed", purpleL: "#faf5ff", purpleB: "#ddd6fe",
  green:  "#15803d", greenL: "#f0fdf4",  greenB:  "#bbf7d0",
  amber:  "#b45309", amberL: "#fffbeb",  amberB:  "#fde68a",
  red:    "#be123c", redL:   "#fff1f2",  redB:    "#fecdd3",
  ink:    "#0f172a", ink2:   "#334155",  ink3:    "#64748b", ink4: "#94a3b8",
  rule:   "#e2e8f0", rule2:  "#f1f5f9",  bg:      "#f8fafc",
  white:  "#ffffff",
};

/* ════════════════════════════════════════════════
   ATOMS
════════════════════════════════════════════════ */
const Spin = ({ size = 13, color = T.blue }) => (
  <span style={{ display: "inline-block", width: size, height: size, flexShrink: 0, border: `2px solid ${color}22`, borderTopColor: color, borderRadius: "50%", animation: "bam-spin .6s linear infinite" }} />
);

const Badge = ({ children, bg, color, border }) => (
  <span style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 20, fontSize: 10.5, fontWeight: 700, background: bg, color, border: `1px solid ${border}` }}>
    {children}
  </span>
);

const Divider = ({ mt = 16, mb = 16 }) => (
  <div style={{ height: 1, background: T.rule, margin: `${mt}px 0 ${mb}px` }} />
);

const StepBadge = ({ n, done, active }) => (
  <div style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0, background: done ? T.green : active ? T.blue : T.rule, color: T.white, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9.5, fontWeight: 800, transition: "background .2s" }}>
    {done ? "✓" : n}
  </div>
);

const StepRow = ({ n, label, done, active }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
    <StepBadge n={n} done={done} active={active} />
    <span style={{ fontSize: 12.5, fontWeight: 700, color: done || active ? T.ink : T.ink4 }}>{label}</span>
  </div>
);

/* ════════════════════════════════════════════════
   LOCATION MULTI-SELECT
════════════════════════════════════════════════ */
const LocSelect = ({ locations, selectedIds, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const all   = locations.length > 0 && selectedIds.length === locations.length;
  const label = disabled         ? "Select auditor type first"
    : !selectedIds.length        ? "Choose locations…"
    : all                        ? "All Locations"
    : selectedIds.length === 1   ? (locations.find((l) => l.id === selectedIds[0])?.unitCode || "1 selected")
    :                              `${selectedIds.length} / ${locations.length} locations`;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        style={{ width: "100%", padding: "9px 12px", fontSize: 13, cursor: disabled ? "not-allowed" : "pointer", border: `1.5px solid ${selectedIds.length ? T.blue : T.rule}`, borderRadius: 8, background: disabled ? T.bg : selectedIds.length ? T.blueL : T.white, color: selectedIds.length ? T.blue : T.ink3, display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: selectedIds.length ? 600 : 400, opacity: disabled ? 0.55 : 1 }}>
        <span>{label}</span>
        <span style={{ fontSize: 9, opacity: 0.5 }}>▾</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 2000, background: T.white, border: `1px solid ${T.rule}`, borderRadius: 10, boxShadow: "0 12px 32px rgba(0,0,0,.13)", maxHeight: 230, overflowY: "auto", padding: "4px 0" }}>
          <label style={{ display: "flex", gap: 8, padding: "8px 13px", cursor: "pointer", fontSize: 12, fontWeight: 700, borderBottom: `1px solid ${T.rule2}`, background: all ? T.blueL : "transparent", alignItems: "center" }}>
            <input type="checkbox" checked={all} onChange={() => onChange(all ? [] : locations.map((l) => l.id))} style={{ accentColor: T.blue, width: 13, height: 13 }} />
            All Locations
          </label>
          {locations.map((loc) => {
            const ch = selectedIds.includes(loc.id);
            return (
              <label key={loc.id} style={{ display: "flex", gap: 8, padding: "7px 13px", cursor: "pointer", fontSize: 12, background: ch ? T.blueL : "transparent", alignItems: "center" }}>
                <input type="checkbox" checked={ch} onChange={() => onChange(ch ? selectedIds.filter((x) => x !== loc.id) : [...selectedIds, loc.id])} style={{ accentColor: T.blue, width: 13, height: 13 }} />
                <span style={{ fontWeight: 600, color: T.ink2 }}>{loc.unitCode}</span>
                {loc.city && <span style={{ color: T.ink4, fontSize: 11 }}>{loc.city}</span>}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════
   KPI DETAIL DRAWER
   Opens from the right when user clicks "N KPIs" link.
   Shows:
     - Question title
     - Existing assignments: Level → Auditor → Locations
     - "Will be assigned fresh" if none
════════════════════════════════════════════════ */
const KpiDrawer = ({ qIds, questionTitles, existingGrouped, allLocations, allAuditors, selectedLocIds, groupLabel, onClose }) => {
  const [search, setSearch] = useState("");
  const getName   = (id) => allAuditors.find((a) => String(a.id) === String(id))?.name || `Auditor #${id}`;
  const getLocName = (id) => allLocations.find((l) => l.id === id)?.unitCode || `Loc #${id}`;

  const filtered = qIds.filter((qid) => {
    const title = (questionTitles[qid] || `KPI #${qid}`).toLowerCase();
    return title.includes(search.toLowerCase());
  });

  return (
    /* Backdrop + drawer wrapper */
    <div style={{ position: "fixed", inset: 0, zIndex: 1400, display: "flex", alignItems: "stretch" }}>
      {/* Dim backdrop */}
      <div onClick={onClose} style={{ flex: 1, background: "rgba(0,0,0,.35)", cursor: "pointer" }} />

      {/* Drawer panel */}
      <div style={{ width: 420, maxWidth: "95vw", background: T.white, display: "flex", flexDirection: "column", boxShadow: "-8px 0 40px rgba(0,0,0,.18)" }}>

        {/* Header */}
        <div style={{ padding: "16px 18px 13px", borderBottom: `1px solid ${T.rule}`, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.ink }}>{groupLabel}</div>
              <div style={{ fontSize: 11, color: T.ink3, marginTop: 2 }}>
                {qIds.length} KPI{qIds.length !== 1 ? "s" : ""} · Assignment details per KPI
              </div>
            </div>
            <button onClick={onClose} style={{ border: `1px solid ${T.rule}`, borderRadius: 8, background: T.bg, width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 15, color: T.ink3 }}>×</button>
          </div>

          {/* Search */}
          <div style={{ marginTop: 10, position: "relative" }}>
            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: T.ink4 }}>🔍</span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search questions…"
              style={{ width: "100%", padding: "7px 10px 7px 30px", fontSize: 12, border: `1px solid ${T.rule}`, borderRadius: 7, background: T.bg, color: T.ink2, boxSizing: "border-box" }}
            />
          </div>
        </div>

        {/* KPI list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "10px 14px" }}>
          {filtered.length === 0 && (
            <div style={{ textAlign: "center", color: T.ink4, fontSize: 12, padding: "30px 0" }}>No KPIs match your search.</div>
          )}

          {filtered.map((qid, i) => {
            /* Collect all existing assignments for this KPI across selected locations */
            const assignments = []; // { locName, level, auditorName }
            selectedLocIds.forEach((lid) => {
              const locData = existingGrouped?.[String(lid)] || {};
              Object.entries(locData).forEach(([lv, slot]) => {
                if ((slot.questionIds || []).map(Number).includes(Number(qid))) {
                  assignments.push({
                    locName:     getLocName(lid),
                    level:       lv,
                    auditorName: getName(slot.auditorId),
                  });
                }
              });
            });

            const title = questionTitles[qid] || `KPI #${qid}`;

            return (
              <div key={qid} style={{ padding: "11px 13px", borderRadius: 9, border: `1px solid ${T.rule}`, marginBottom: 8, background: i % 2 === 0 ? T.white : T.bg }}>
                {/* Title */}
                <div style={{ fontSize: 12.5, fontWeight: 700, color: T.ink, lineHeight: 1.45, marginBottom: 7 }}>
                  {title}
                </div>

                {assignments.length === 0 ? (
                  /* Fresh KPI */
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: T.green, fontWeight: 600 }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: T.green, display: "inline-block", flexShrink: 0 }} />
                    No existing assignments — will be assigned fresh
                  </div>
                ) : (
                  /* Already has assignments */
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: T.ink4, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 5 }}>
                      Current assignments
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {assignments.map((a, ai) => (
                        <div key={ai} style={{ display: "flex", alignItems: "center", gap: 7, padding: "5px 9px", borderRadius: 6, background: T.blueL, border: `1px solid ${T.blueB}` }}>
                          {/* Level circle */}
                          <div style={{ minWidth: 22, height: 22, borderRadius: "50%", background: T.blue, color: T.white, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, flexShrink: 0 }}>
                            L{a.level}
                          </div>
                          {/* Auditor name */}
                          <span style={{ fontSize: 11.5, fontWeight: 600, color: T.ink2, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {a.auditorName}
                          </span>
                          {/* Location pill */}
                          <span style={{ fontSize: 10, color: T.ink3, background: T.white, padding: "1px 7px", borderRadius: 20, border: `1px solid ${T.rule}`, whiteSpace: "nowrap", flexShrink: 0 }}>
                            📍 {a.locName}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: "10px 18px", borderTop: `1px solid ${T.rule}`, flexShrink: 0, background: T.bg }}>
          <button onClick={onClose} style={{ width: "100%", padding: "8px 0", borderRadius: 8, border: `1.5px solid ${T.rule}`, background: T.white, color: T.ink2, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            ← Back to Assignment
          </button>
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════
   EXISTING SUMMARY (inside modal body)
   Single hyperlink — clicking opens KpiDrawer with
   all already-assigned KPIs across all selected locations.
════════════════════════════════════════════════ */
const ExistingSummary = ({ grouped, locations, selectedLocIds, loading, onViewKpis }) => {
  if (loading) return (
    <div style={{ padding: "9px 12px", borderRadius: 7, background: T.bg, border: `1px solid ${T.rule}`, display: "flex", gap: 8, fontSize: 11.5, color: T.ink3, marginBottom: 12, alignItems: "center" }}>
      <Spin size={12} /> Checking existing assignments…
    </div>
  );
  if (!grouped) return null;

  const lids = selectedLocIds.filter((lid) => Object.keys(grouped[String(lid)] || {}).length > 0);

  if (!lids.length) return (
    <div style={{ padding: "8px 12px", borderRadius: 7, background: T.greenL, border: `1px solid ${T.greenB}`, fontSize: 11.5, color: T.green, marginBottom: 12, display: "flex", gap: 6, alignItems: "center" }}>
      <span style={{ fontSize: 14 }}>✓</span> No existing assignments for selected locations — all KPIs will be assigned fresh.
    </div>
  );

  // Collect all unique KPI IDs across every location + level
  const allQIds = [...new Set(
    lids.flatMap((lid) =>
      Object.values(grouped[String(lid)]).flatMap((slot) => (slot.questionIds || []).map(Number))
    )
  )];
  const drawerLabel = `Existing Assignments — ${allQIds.length} KPI${allQIds.length !== 1 ? "s" : ""} across ${lids.length} location${lids.length !== 1 ? "s" : ""}`;

  return (
    <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 7, padding: "8px 12px", borderRadius: 7, background: T.amberL, border: `1px solid ${T.amberB}` }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.amber, flexShrink: 0 }} />
      <span style={{ fontSize: 12, color: T.amber, fontWeight: 600 }}>
        Some KPIs already have assignments.
      </span>
      <button
        onClick={() => onViewKpis && onViewKpis(allQIds, drawerLabel)}
        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 12, fontWeight: 700, color: T.blue, textDecoration: "underline", textUnderlineOffset: 2, whiteSpace: "nowrap" }}
      >
        View existing assignments →
      </button>
    </div>
  );
};

/* ════════════════════════════════════════════════
   MAIN MODAL
════════════════════════════════════════════════ */
const BulkAuditAssignModal = ({ show, onHide, selectedIds = [], financialYearId, onSuccess }) => {
  const isMounted = useRef(true);
  const { companyId } = useCurrentUser();
  useEffect(() => () => { isMounted.current = false; }, []);

  /* ── All data state ── */
  const [allLocations,    setAllLocations]    = useState([]);
  const [allAuditors,     setAllAuditors]     = useState([]);
  const [existingGrouped, setExistingGrouped] = useState(null);
  const [questionTitles,  setQuestionTitles]  = useState({});
  const [loadingInit,     setLoadingInit]     = useState(false);
  const [loadingAuditors, setLoadingAuditors] = useState(false);

  /* ── Form state ── */
  const [auditorType,    setAuditorType]    = useState("");
  const [selectedLocIds, setSelectedLocIds] = useState([]);
  const [levelGroups,    setLevelGroups]    = useState([]);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState("");

  /* ── Drawer state ── */
  const [drawer, setDrawer] = useState(null); // { qIds, label }

  /* ──────────────────────────────────────────────────────────
     SINGLE API CALL ON OPEN
  ────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!show) return;

    /* Reset state */
    setAuditorType(""); setSelectedLocIds([]); setLevelGroups([]);
    setAllAuditors([]); setExistingGrouped(null);
    setError(""); setSuccess(""); setDrawer(null);

    setLoadingInit(true);

    /* Fetch ONLY locations first */
    apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getSource`, {}, {}, "GET")
      .then((locRes) => {
        if (locRes.isSuccess && isMounted.current) {
          setAllLocations((locRes.data?.data || []).map((l) => ({
            id: l.id,
            unitCode: l.unitCode || `Loc ${l.id}`,
            city: l.location?.city || "",
          })));
        }
      })
      .finally(() => { if (isMounted.current) setLoadingInit(false); });
  }, [show]);

  /* ── Fetch existing config only when locations are selected ── */
  useEffect(() => {
    if (!show || !selectedLocIds.length || !selectedIds.length) {
      setExistingGrouped(null);
      return;
    }

    setLoadingInit(true);
    apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}getExistingAssignmentConfig`,
      {},
      {
        questionIds:     selectedIds,
        locationIds:     selectedLocIds,
        financialYearId,
        auditorType:     auditorType || "",
      },
      "POST"
    ).then((existRes) => {
      if (existRes.isSuccess && isMounted.current) {
        setExistingGrouped(existRes.data?.data?.grouped || {});
        if (existRes.data?.data?.questionTitles) setQuestionTitles(existRes.data.data.questionTitles);
      }
    }).finally(() => { if (isMounted.current) setLoadingInit(false); });
  }, [selectedLocIds, auditorType, show]);

  /* ── When auditor type changes → fetch users ── */
  useEffect(() => {
    if (!auditorType) return;
    setAllAuditors([]); setSelectedLocIds([]); setLevelGroups([]);
    setLoadingAuditors(true);

    apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}getSubUserBasedOnRoleId`, {},
      { roleId: ROLE_IDS[auditorType] }, "GET"
    ).then(({ isSuccess, data }) => {
      if (isSuccess && isMounted.current) {
        setAllAuditors((data?.data || []).map((u) => ({
          id:          String(u.userId),
          name:        [u.firstName, u.lastName].filter(Boolean).join(" ") + (u.designation ? ` (${u.designation})` : ""),
          locationIds: (u.location || []).map((l) => l.sourceId),
        })));
      }
    }).finally(() => { if (isMounted.current) setLoadingAuditors(false); });
  }, [auditorType]);

  /* ── When locations change → compute groups CLIENT-SIDE ── */
  useEffect(() => {
    if (!selectedLocIds.length || !existingGrouped) { setLevelGroups([]); return; }

    const buckets = {};

    selectedIds.forEach((qid) => {
      let startingLevel = 1;

      const assignedLevelsInSelectedLocs = [];
      selectedLocIds.forEach((lid) => {
        const locData = existingGrouped[String(lid)] || {};
        Object.entries(locData).forEach(([lv, slot]) => {
          if ((slot.questionIds || []).map(Number).includes(Number(qid))) {
            assignedLevelsInSelectedLocs.push(Number(lv));
          }
        });
      });

      if (assignedLevelsInSelectedLocs.length > 0) {
        const minMissingLevel = Array.from({ length: MAX_LEVELS }, (_, i) => i + 1)
          .find(lv => {
            return !selectedLocIds.every(lid => {
              const locData = existingGrouped[String(lid)] || {};
              return (locData[String(lv)]?.questionIds || []).map(Number).includes(Number(qid));
            });
          });

        startingLevel = minMissingLevel || (MAX_LEVELS + 1);
      } else {
        startingLevel = 1;
      }

      if (startingLevel > MAX_LEVELS) return;

      buckets[startingLevel] = buckets[startingLevel] || [];
      buckets[startingLevel].push(qid);
    });

    const groups = Object.entries(buckets)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([lvl, qids]) => ({
        nextLevel: Number(lvl),
        qIds:      qids,
        rows:      [{ level: Number(lvl), auditorId: "", overwrite: false }],
      }));

    setLevelGroups(groups);
  }, [selectedLocIds, existingGrouped, selectedIds]);

  /* ── Auditors common to ALL selected locations ── */
  const commonAuditors = useMemo(() => {
    if (!selectedLocIds.length) return allAuditors;
    return allAuditors.filter((u) => selectedLocIds.every((lid) => u.locationIds.includes(lid)));
  }, [allAuditors, selectedLocIds]);

  /* ── Level helpers ── */
  const addLevel = (gIdx) =>
    setLevelGroups((prev) => prev.map((g, i) => {
      if (i !== gIdx) return g;
      const curMax = g.rows.length ? Math.max(...g.rows.map((r) => r.level)) : g.nextLevel - 1;
      if (curMax >= MAX_LEVELS) return g;
      return { ...g, rows: [...g.rows, { level: curMax + 1, auditorId: "", overwrite: false }] };
    }));

  const removeLevel = (gIdx, rIdx) =>
    setLevelGroups((prev) => prev.map((g, i) =>
      i !== gIdx ? g : { ...g, rows: g.rows.filter((_, ri) => ri !== rIdx) }
    ));

  const updateRow = (gIdx, rIdx, patch) =>
    setLevelGroups((prev) => prev.map((g, i) =>
      i !== gIdx ? g : { ...g, rows: g.rows.map((r, ri) => ri !== rIdx ? r : { ...r, ...patch }) }
    ));

  /* ── Per-row validation ── */
  const rowErrors = useMemo(() => {
    const errors = {};
    levelGroups.forEach((group, gIdx) => {
      const seen = new Map();
      group.rows.forEach((row, rIdx) => {
        if (!row.auditorId) return;
        const key = `${gIdx}-${rIdx}`;
        if (seen.has(row.auditorId)) {
          errors[key] = `Already used at Level ${seen.get(row.auditorId)} — same auditor cannot appear twice.`;
        } else {
          seen.set(row.auditorId, row.level);
        }
        selectedLocIds.forEach((lid) => {
          const locData = existingGrouped?.[String(lid)] || {};
          Object.entries(locData).forEach(([lv, slot]) => {
            if (String(slot.auditorId) === String(row.auditorId) && Number(lv) !== row.level) {
              errors[key] = `This auditor already has Level ${lv} assignments at ${allLocations.find((l) => l.id === lid)?.unitCode || `Loc #${lid}`}.`;
            }
          });
        });
      });
    });
    return errors;
  }, [levelGroups, selectedLocIds, existingGrouped, allLocations]);

  const hasErrors   = Object.keys(rowErrors).length > 0;
  const allFilled   = levelGroups.length > 0 && levelGroups.some((g) => g.rows.length > 0 && g.rows.every((r) => r.auditorId));
  const allAssigned = !loadingInit && selectedLocIds.length > 0 && levelGroups.length === 0 && !!existingGrouped;

  /* ── Save ── */
  const handleSave = async () => {
    setError(""); setSuccess("");
    if (!auditorType)           { setError("Select an auditor type."); return; }
    if (!selectedLocIds.length) { setError("Select at least one location."); return; }
    if (!allFilled)             { setError("Select an auditor for every level."); return; }
    if (hasErrors)              { setError(Object.values(rowErrors)[0]); return; }

    setSaving(true);
    try {
      for (const group of levelGroups) {
        if (!group.rows.length) continue;
        await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}assignAuditorsToQuestions`, {},
          {
            financialYearId, companyId,
            questionIds:   group.qIds,
            locationIds:   selectedLocIds,
            questionnaire_type: "CA",
            ...(auditorType === "INTERNAL"
              ? { internalLevels: group.rows.map((r) => ({ level: r.level, auditorId: r.auditorId, overwrite: r.overwrite })) }
              : { externalLevels: group.rows.map((r) => ({ level: r.level, auditorId: r.auditorId, overwrite: r.overwrite })) }),
          },
          "POST"
        );
      }
      setSuccess(`✓ Auditors assigned for ${selectedIds.length} KPI${selectedIds.length !== 1 ? "s" : ""}.`);
      setTimeout(() => { onSuccess?.(); onHide(); }, 1500);
    } catch (e) { setError(e?.message || "Server error."); }
    finally     { if (isMounted.current) setSaving(false); }
  };

  if (!show) return null;

  const step1Done  = !!auditorType;
  const step2Done  = selectedLocIds.length > 0;
  const typeColor  = auditorType === "EXTERNAL" ? T.purple : T.blue;
  const typeLight  = auditorType === "EXTERNAL" ? T.purpleL : T.blueL;
  const typeBorder = auditorType === "EXTERNAL" ? T.purpleB : T.blueB;

  return (
    <>
      <style>{`@keyframes bam-spin { to { transform: rotate(360deg); } }`}</style>

      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.48)", backdropFilter: "blur(4px)", zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
        <div style={{ background: T.white, borderRadius: 16, width: "100%", maxWidth: 540, maxHeight: "94vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 64px rgba(0,0,0,.22)" }}>

          {/* ── Header ── */}
          <div style={{ padding: "16px 20px 14px", borderBottom: `1px solid ${T.rule}`, display: "flex", alignItems: "center", gap: 11, flexShrink: 0 }}>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: T.blueL, border: `1.5px solid ${T.blueB}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>🔍</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: T.ink, letterSpacing: "-0.01em" }}>Assign Auditors</div>
              <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 2, display: "flex", alignItems: "center", gap: 5 }}>
                <span>{selectedIds.length} KPI{selectedIds.length !== 1 ? "s" : ""} selected</span>
                {step1Done && <><span style={{ color: T.rule }}>·</span><span style={{ color: typeColor, fontWeight: 600 }}>{auditorType === "INTERNAL" ? "Internal" : "External"}</span></>}
                {step2Done && <><span style={{ color: T.rule }}>·</span><span>{selectedLocIds.length} location{selectedLocIds.length !== 1 ? "s" : ""}</span></>}
                {loadingInit && <><span style={{ color: T.rule }}>·</span><Spin size={10} /></>}
              </div>
            </div>
            <button onClick={onHide} disabled={saving} style={{ border: `1px solid ${T.rule}`, borderRadius: 8, background: T.bg, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 16, color: T.ink3 }}>×</button>
          </div>

          {/* ── Body ── */}
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>

            {error   && <div style={{ padding: "9px 13px", borderRadius: 8, background: T.redL,   border: `1px solid ${T.redB}`,   color: T.red,   fontSize: 12.5, marginBottom: 14, display: "flex", gap: 8 }}><span>⚠️</span>{error}</div>}
            {success && <div style={{ padding: "9px 13px", borderRadius: 8, background: T.greenL, border: `1px solid ${T.greenB}`, color: T.green, fontSize: 12.5, marginBottom: 14, display: "flex", gap: 8 }}><span>✅</span>{success}</div>}

            {/* ══ STEP 1: Auditor Type ══ */}
            <StepRow n={1} label="Select Auditor Type" done={step1Done} active={!step1Done} />
            <div style={{ marginBottom: 22 }}>
              <select
                value={auditorType}
                onChange={(e) => setAuditorType(e.target.value)}
                style={{ width: "100%", padding: "10px 12px", fontSize: 13, cursor: "pointer", border: `1.5px solid ${step1Done ? typeColor : T.rule}`, borderRadius: 9, background: step1Done ? typeLight : T.white, color: step1Done ? typeColor : T.ink2, fontWeight: step1Done ? 700 : 400, appearance: "auto", outline: "none" }}
              >
                <option value="">— Select auditor type —</option>
                <option value="INTERNAL">Internal Auditor</option>
                <option value="EXTERNAL">External Auditor (runs after all internal levels)</option>
              </select>

              {step1Done && (
                <div style={{ marginTop: 8, padding: "7px 11px", borderRadius: 7, background: typeLight, border: `1px solid ${typeBorder}`, fontSize: 11.5, color: typeColor, display: "flex", alignItems: "center", gap: 7 }}>
                  {loadingAuditors
                    ? <><Spin size={11} color={typeColor} /> Loading auditors…</>
                    : <><span style={{ width: 7, height: 7, borderRadius: "50%", background: typeColor, display: "inline-block", flexShrink: 0 }} /><b>{allAuditors.length}</b> {auditorType.toLowerCase()} auditor{allAuditors.length !== 1 ? "s" : ""} found</>
                  }
                </div>
              )}
            </div>

            {/* ══ STEP 2: Locations ══ */}
            <StepRow n={2} label="Select Locations" done={step2Done} active={step1Done && !step2Done} />
            <div style={{ marginBottom: 18, opacity: step1Done ? 1 : 0.45, pointerEvents: step1Done ? "auto" : "none" }}>
              <div style={{ fontSize: 11.5, color: T.ink3, marginBottom: 7 }}>
                Only auditors present at <b>all</b> selected locations will be shown.
              </div>
              <LocSelect locations={allLocations} selectedIds={selectedLocIds} onChange={setSelectedLocIds} disabled={!step1Done} />

              {selectedLocIds.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
                  {selectedLocIds.map((id) => {
                    const loc = allLocations.find((l) => l.id === id);
                    return (
                      <span key={id} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px 3px 10px", borderRadius: 20, background: T.blueL, border: `1px solid ${T.blueB}`, fontSize: 11.5, fontWeight: 600, color: T.blue }}>
                        {loc?.unitCode || `#${id}`}
                        <button onClick={() => setSelectedLocIds((p) => p.filter((x) => x !== id))} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#93c5fd", fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
                      </span>
                    );
                  })}
                </div>
              )}

              {step2Done && (
                <div style={{ marginTop: 9, padding: "7px 11px", borderRadius: 7, fontSize: 12, fontWeight: 600, background: commonAuditors.length ? T.greenL : T.amberL, border: `1px solid ${commonAuditors.length ? T.greenB : T.amberB}`, color: commonAuditors.length ? T.green : T.amber, display: "flex", alignItems: "center", gap: 7 }}>
                  {loadingAuditors
                    ? <><Spin size={11} color={T.green} /> Loading…</>
                    : commonAuditors.length
                      ? <>✓ {commonAuditors.length} auditor{commonAuditors.length !== 1 ? "s" : ""} available at all selected locations</>
                      : <>⚠ No auditors from this role at all selected locations — try fewer locations</>
                  }
                </div>
              )}
            </div>

            {/* Existing assignments summary */}
            {step2Done && (
              <ExistingSummary
                grouped={existingGrouped}
                locations={allLocations}
                selectedLocIds={selectedLocIds}
                loading={loadingInit}
                onViewKpis={(qIds, label) => setDrawer({ qIds, label })}
              />
            )}

            {/* ══ STEP 3: Level Groups ══ */}
            {step2Done && (
              <>
                <StepRow n={3} label="Configure Levels" done={allFilled && !hasErrors} active={step2Done && levelGroups.length > 0} />

                {/* All assigned */}
                {allAssigned && (
                  <div style={{ padding: "11px 14px", borderRadius: 8, background: T.greenL, border: `1px solid ${T.greenB}`, fontSize: 12.5, color: T.green }}>
                    ✓ All selected KPIs already have all {MAX_LEVELS} levels configured at these locations.
                  </div>
                )}

                {/* No groups yet */}
                {!loadingInit && levelGroups.length === 0 && !allAssigned && (
                  <div style={{ padding: "11px 14px", borderRadius: 8, background: T.bg, border: `1px solid ${T.rule}`, fontSize: 12, color: T.ink3 }}>
                    KPI groups will appear here once locations are selected.
                  </div>
                )}

                {/* Render groups */}
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {levelGroups.map((group, gIdx) => {
                    const curMax     = group.rows.length ? Math.max(...group.rows.map((r) => r.level)) : group.nextLevel - 1;
                    const canAddMore = curMax < MAX_LEVELS;

                    const freshCount = group.qIds.filter((qid) =>
                      !selectedLocIds.some((lid) => {
                        const d = existingGrouped?.[String(lid)] || {};
                        return Object.values(d).some((s) => (s.questionIds || []).map(Number).includes(Number(qid)));
                      })
                    ).length;
                    const withPriorCount = group.qIds.length - freshCount;

                    return (
                      <div key={gIdx} style={{ border: `1.5px solid ${T.rule}`, borderRadius: 12, overflow: "hidden", background: T.white }}>

                        {/* Group header */}
                        <div style={{ padding: "10px 14px 9px", background: T.bg, borderBottom: `1px solid ${T.rule}`, display: "flex", alignItems: "flex-start", gap: 10 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              <button
                                onClick={() => setDrawer({ qIds: group.qIds, label: `${group.qIds.length} KPI${group.qIds.length !== 1 ? "s" : ""} · Level ${group.nextLevel} group` })}
                                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: 13, fontWeight: 700, color: T.blue, textDecoration: "underline", textUnderlineOffset: 2, textDecorationColor: T.blueB }}
                              >
                                {group.qIds.length} KPI{group.qIds.length !== 1 ? "s" : ""}
                              </button>
                              <span style={{ color: T.ink4, fontSize: 12 }}>starting at</span>
                              <span style={{ padding: "2px 9px", borderRadius: 20, background: typeLight, color: typeColor, border: `1px solid ${typeBorder}`, fontSize: 11.5, fontWeight: 700 }}>
                                Level {group.nextLevel}
                              </span>
                            </div>

                            <div style={{ marginTop: 4, display: "flex", gap: 10, flexWrap: "wrap" }}>
                              {freshCount > 0 && (
                                <span style={{ fontSize: 11, color: T.green, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.green, display: "inline-block" }} />
                                  {freshCount} fresh
                                </span>
                              )}
                              {withPriorCount > 0 && (
                                <span style={{ fontSize: 11, color: T.ink4, fontWeight: 600 }}>
                                  · {withPriorCount} already have earlier levels
                                </span>
                              )}
                            </div>
                          </div>

                          <button
                            onClick={() => setDrawer({ qIds: group.qIds, label: `${group.qIds.length} KPI${group.qIds.length !== 1 ? "s" : ""} · Level ${group.nextLevel} group` })}
                            style={{ fontSize: 11, fontWeight: 600, color: T.blue, background: T.blueL, border: `1px solid ${T.blueB}`, borderRadius: 20, padding: "4px 11px", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}
                          >
                            View KPIs →
                          </button>
                        </div>

                        {/* Level rows */}
                        <div style={{ padding: "10px 14px", display: "flex", flexDirection: "column", gap: 7 }}>
                          {group.rows.map((row, rIdx) => {
                            const isLast    = rIdx === group.rows.length - 1;
                            const err       = rowErrors[`${gIdx}-${rIdx}`];
                            const available = commonAuditors.filter((u) => {
                              const usedInGroup = group.rows.map((r) => r.auditorId).filter(Boolean);
                              return !usedInGroup.includes(u.id) || u.id === row.auditorId;
                            });

                            return (
                              <div key={rIdx} style={{ padding: "9px 11px", borderRadius: 8, background: err ? T.redL : "#fcfcfd", border: `1.5px solid ${err ? T.redB : T.rule}`, display: "flex", flexDirection: "column", gap: 6 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <div style={{ minWidth: 36, height: 36, borderRadius: "50%", background: typeLight, color: typeColor, border: `2px solid ${typeColor}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, flexShrink: 0 }}>
                                    L{row.level}
                                  </div>
                                  <div style={{ flexShrink: 0 }}>
                                    <div style={{ fontSize: 11.5, fontWeight: 700, color: typeColor }}>Level {row.level}</div>
                                    <div style={{ fontSize: 9.5, color: T.ink4 }}>{auditorType === "INTERNAL" ? "Internal" : "External"}</div>
                                  </div>

                                  <select
                                    value={row.auditorId}
                                    onChange={(e) => updateRow(gIdx, rIdx, { auditorId: e.target.value })}
                                    disabled={!commonAuditors.length}
                                    style={{ flex: 1, padding: "7px 10px", border: `1.5px solid ${row.auditorId ? typeBorder : T.rule}`, borderRadius: 7, fontSize: 12.5, background: row.auditorId ? typeLight : T.white, color: row.auditorId ? typeColor : T.ink2, cursor: commonAuditors.length ? "pointer" : "not-allowed", fontWeight: row.auditorId ? 600 : 400, outline: "none" }}
                                  >
                                    <option value="">{!commonAuditors.length ? "No auditors at selected locations" : "— Select auditor —"}</option>
                                    {available.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                                  </select>

                                  {isLast
                                    ? <button onClick={() => removeLevel(gIdx, rIdx)} style={{ border: "none", background: "transparent", color: T.ink4, cursor: "pointer", fontSize: 19, lineHeight: 1, flexShrink: 0 }}>×</button>
                                    : <div style={{ width: 22, flexShrink: 0 }} />
                                  }
                                </div>

                                {err && (
                                  <div style={{ fontSize: 11, color: T.red, paddingLeft: 44, display: "flex", gap: 5 }}>
                                    <span>⚠</span><span>{err}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {canAddMore && (
                            <button
                              onClick={() => addLevel(gIdx)}
                              style={{ padding: "8px 0", borderRadius: 8, border: `1.5px dashed ${typeBorder}`, background: "transparent", color: typeColor, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                              onMouseEnter={(e) => e.currentTarget.style.background = typeLight}
                              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                            >
                              <span style={{ width: 17, height: 17, borderRadius: "50%", background: typeColor, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 900, flexShrink: 0 }}>+</span>
                              Add Level {curMax + 1}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Legend */}
            <div style={{ marginTop: 16, padding: "9px 12px", borderRadius: 8, background: T.bg, border: `1px solid ${T.rule}`, fontSize: 10.5, color: T.ink4, lineHeight: 1.9 }}>
              <div>• Click the <b>KPI count link</b> or <b>View KPIs →</b> to see question details and current assignments.</div>
              <div>• KPIs are grouped by their <b>next available level</b> — configure each group independently.</div>
              <div>• Same auditor <b>cannot appear at two levels</b> for the same KPI and location.</div>
              <div>• Levels are <b>sequential</b> — L2 requires L1 to already exist.</div>
            </div>
          </div>

          {/* ── Footer ── */}
          <div style={{ padding: "12px 20px", borderTop: `1px solid ${T.rule}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", background: T.bg, borderRadius: "0 0 16px 16px" }}>
            <div style={{ fontSize: 11.5, color: hasErrors ? T.red : T.ink3 }}>
              {hasErrors ? `${Object.keys(rowErrors).length} validation error${Object.keys(rowErrors).length !== 1 ? "s" : ""}`
                : allFilled ? `${levelGroups.length} group${levelGroups.length !== 1 ? "s" : ""} ready`
                : ""}
            </div>
            <div style={{ display: "flex", gap: 9 }}>
              <button onClick={onHide} disabled={saving} style={{ padding: "8px 18px", borderRadius: 8, border: `1.5px solid ${T.rule}`, background: T.white, color: T.ink2, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button
                onClick={handleSave}
                disabled={saving || !step2Done || !allFilled || hasErrors}
                style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: (saving || !step2Done || !allFilled || hasErrors) ? "#93c5fd" : T.blue, color: T.white, fontSize: 13, fontWeight: 700, cursor: (saving || !step2Done || !allFilled || hasErrors) ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 7 }}
              >
                {saving ? <><Spin size={12} color={T.white} /> Saving…</> : "Save Assignments"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── KPI Detail Drawer ── */}
      {drawer && (
        <KpiDrawer
          qIds={drawer.qIds}
          groupLabel={drawer.label}
          questionTitles={questionTitles}
          existingGrouped={existingGrouped}
          allLocations={allLocations}
          allAuditors={allAuditors}
          selectedLocIds={selectedLocIds}
          onClose={() => setDrawer(null)}
        />
      )}
    </>
  );
};

export default BulkAuditAssignModal;